import { useCallback, useMemo, useReducer } from "react";

import { gql, useMutation } from "@apollo/client";

import { convertToMarkdown } from "@/components/helpers/string-utils";
import { TagItemInput } from "@/components/pieces/roleplays/TagInput";
import {
  ScenarioDifficulty,
  ScenarioDocument,
  useEditScenarioMutation,
} from "@/graphql";
import { UploadedFile } from "@/lib/upload-file";
import { ListScenario } from "@/types/roleplays";
import { Tab } from "@/types/tabs";

interface state {
  context?: string;
  objective?: string;
  name?: string;
  description?: string;
  error?: string | null;
  tab: Tab;
  difficulty?: ScenarioDifficulty;
  introductionScript?: string;
  introductionAudioUrl?: string;
  persona: {
    name: string;
    firstName: string;
    lastName: string;
    photo?: string;
    characterHeadshotId?: string;
    characterHeadshotUrl?: string;
    voiceId: string;
    openingLine?: string;
    personalityTraits: {
      [key: string]: string;
    };
    personalityCharacteristics?: string[];
    isTraitSelector: boolean;
    jobTitle?: string;
    company?: string;
    backstory?: string;
    situationalUnderstanding?: string;
  };
  audioPreview: "generated" | "generating" | "loaded" | "dirty" | "none";
  tags?: TagItemInput[];
  presentations: UploadedFile[];
  enableScreenSharing: boolean;
}

type action =
  | {
      type: "setError";
      error: string;
      initialScenario: ListScenario;
      audioPreview?: "none";
    }
  | {
      type: "setTab";
      tab: Tab;
    }
  | {
      type: "setDescription";
      description: string;
    }
  | {
      type: "setDifficulty";
      difficulty: ScenarioDifficulty;
    }
  | {
      type: "setPersonalityTraits";
      traits: {
        [key: string]: string;
      };
    }
  | {
      type: "setVoiceId";
      voiceId: string;
    }
  | {
      type: "setOpeningLine";
      openingLine: string;
    }
  | {
      type: "setPersonaName";
      name: string;
    }
  | {
      type: "setPersonalityCharacteristics";
      characteristics: string[];
    }
  | {
      type: "setContext";
      context: string;
    }
  | {
      type: "setObjective";
      objective: string;
    }
  | {
      type: "setName";
      name: string;
    }
  | {
      type: "switchToStructured";
    }
  | {
      type: "setPhoto";
      photo: string;
    }
  | {
      type: "setJobTitle";
      jobTitle: string;
    }
  | {
      type: "setCompany";
      company: string;
    }
  | {
      type: "setBackstory";
      backstory: string;
    }
  | {
      type: "setSituationalUnderstanding";
      situationalUnderstanding: string;
    }
  | {
      type: "setIntroductionScript";
      introductionScript: string;
    }
  | {
      type: "setIntroductionAudioUrl";
      introductionAudioUrl: string;
    }
  | {
      type: "setAudioPreview";
      audioPreview: "generated" | "generating" | "loaded" | "dirty" | "none";
    }
  | {
      type: "setTags";
      payload: TagItemInput[];
    }
  | {
      type: "sortPresentationFiles";
      payload: UploadedFile[];
    }
  | {
      type: "addPresentationFile";
      payload: UploadedFile;
    }
  | {
      type: "removePresentationFile";
      payload: { fileId: string };
    }
  | {
      type: "setEnableScreenSharing";
      payload: boolean;
    }
  | {
      type: "setCharacterHeadshot";
      headshotId: string;
      headshotData?: {
        id: string;
        imageUrlEasy?: string | null;
        imageUrlMedium?: string | null;
        imageUrlHard?: string | null;
        imageUrlVeryHard?: string | null;
        imageUrlDefault?: string | null;
      };
    };

const reducer = (state: state, action: action): state => {
  switch (action.type) {
    case "setContext":
      return { ...state, context: action.context, error: null };
    case "setObjective":
      return { ...state, objective: action.objective, error: null };
    case "setName":
      return { ...state, name: action.name, error: null };
    case "setError":
      return {
        ...state,
        ...stateFromScenario(
          action.initialScenario!,
          state.tab.key as "basic" | "scenario" | "character" | null,
        ),
        error: action.error,
        audioPreview: action.audioPreview ?? state.audioPreview,
      };
    case "setTab":
      return { ...state, tab: action.tab };
    case "setDescription":
      return {
        ...state,
        description: action.description,
        error: null,
      };
    case "setDifficulty":
      return { ...state, difficulty: action.difficulty, error: null };
    case "setPersonalityTraits":
      return {
        ...state,
        persona: { ...state.persona, personalityTraits: action.traits },
        error: null,
      };
    case "setVoiceId":
      return {
        ...state,
        persona: {
          ...state.persona,
          voiceId: action.voiceId,
        },
        error: null,
      };
    case "setOpeningLine":
      return {
        ...state,
        persona: { ...state.persona, openingLine: action.openingLine },
        error: null,
      };
    case "setPersonaName":
      return {
        ...state,
        persona: {
          ...state.persona,
          name: action.name,
        },
        error: null,
      };
    case "setPersonalityCharacteristics":
      return {
        ...state,
        persona: {
          ...state.persona,
          personalityCharacteristics: action.characteristics,
        },
        error: null,
      };
    case "switchToStructured":
      return {
        ...state,
        persona: {
          ...state.persona,
          isTraitSelector: true,
        },
        error: null,
      };
    case "setPhoto":
      return {
        ...state,
        persona: {
          ...state.persona,
          photo: action.photo,
          characterHeadshotId: undefined, // Clear library headshot when custom photo is uploaded
          characterHeadshotUrl: undefined, // Clear library headshot URL as well
        },
      };
    case "setJobTitle":
      return {
        ...state,
        persona: { ...state.persona, jobTitle: action.jobTitle },
        error: null,
      };
    case "setCompany":
      return {
        ...state,
        persona: { ...state.persona, company: action.company },
        error: null,
      };
    case "setBackstory":
      return {
        ...state,
        persona: { ...state.persona, backstory: action.backstory },
        error: null,
      };
    case "setSituationalUnderstanding":
      return {
        ...state,
        persona: {
          ...state.persona,
          situationalUnderstanding: action.situationalUnderstanding,
        },
        error: null,
      };
    case "setIntroductionScript":
      return {
        ...state,
        introductionScript: action.introductionScript,
        audioPreview:
          action.introductionScript.trim() === state.introductionScript?.trim()
            ? ("loaded" as const)
            : ("dirty" as const),
        error: null,
      };
    case "setIntroductionAudioUrl":
      return {
        ...state,
        introductionAudioUrl: action.introductionAudioUrl,
        audioPreview: "generated" as const,
        error: null,
      };
    case "setAudioPreview":
      return {
        ...state,
        audioPreview: action.audioPreview,
        error: null,
      };

    case "setTags":
      return {
        ...state,
        tags: action.payload,
      };

    case "sortPresentationFiles":
      return {
        ...state,
        presentations: action.payload,
      };

    case "addPresentationFile":
      return {
        ...state,
        presentations: [action.payload, ...state.presentations],
      };

    case "removePresentationFile":
      return {
        ...state,
        presentations: state.presentations.filter(
          (file) => file.id !== action.payload.fileId,
        ),
      };

    case "setEnableScreenSharing":
      return {
        ...state,
        enableScreenSharing: action.payload,
      };
    case "setCharacterHeadshot":
      return {
        ...state,
        persona: {
          ...state.persona,
          characterHeadshotId: action.headshotId,
          characterHeadshotUrl:
            action.headshotData?.imageUrlDefault || undefined,
          photo: undefined, // Clear custom photo when library headshot is selected
        },
        error: null,
      };
  }
};

function stateFromScenario(
  scenario: ListScenario,
  initialTab: "basic" | "scenario" | "character" | null,
): state {
  return {
    context: scenario?.rawContext ?? "",
    objective: scenario?.rawObjective ?? "",
    name: scenario?.name ?? "",
    description: scenario?.shortDesc ?? "",
    introductionScript: scenario?.introductionScript ?? "",
    introductionAudioUrl: scenario?.introductionAudioUrl ?? "",
    difficulty: scenario?.difficulty ?? ScenarioDifficulty.Custom,
    tab: { key: initialTab ?? "basic" },
    audioPreview: scenario?.introductionAudioUrl ? "loaded" : "none",
    persona: {
      name: scenario?.personaName ?? "",
      firstName: scenario?.personaFirstName ?? "",
      lastName: scenario?.personaLastName ?? "",
      photo: scenario?.personaPhotoUrl ?? "",
      voiceId: scenario?.voiceId ?? "",
      personalityTraits: Object.fromEntries(
        scenario?.personalityTraits?.map((trait) => [
          trait.attributeId,
          trait.levelId,
        ]) ?? [],
      ),
      personalityCharacteristics:
        scenario?.personalityCharacteristics
          ?.split("\n")
          .map((line) => line.replace(/^[-*]\s+/, "").trim()) ?? [],
      isTraitSelector: scenario?.personalityTraits?.length > 0,
      openingLine: scenario?.personaOpeningLine ?? "",
      jobTitle: scenario?.personaJobTitle ?? "",
      company: scenario?.personaCompany ?? "",
      backstory: scenario?.personaBackstory ?? "",
      situationalUnderstanding: scenario?.personaSituationalUnderstanding ?? "",
    },
    tags:
      scenario?.tags?.map((tag) => ({
        value: tag?.id ?? "",
        label: tag?.name ?? "",
      })) ?? [],
    presentations: scenario?.presentations.map((presentation) => ({
      id: presentation.file.id,
      name: presentation.file.name,
      url: presentation.file.url,
      size: presentation.file.size,
      type: presentation.file.contentType ?? "Unknown",
    })),
    enableScreenSharing: scenario?.enableScreenSharing ?? false,
  };
}
const GENERATE_INTRODUCTION_AUDIO_PREVIEW_MUTATION = gql`
  mutation GenerateIntroductionAudioPreviewMutation(
    $scenarioName: String!
    $introductionScript: String!
  ) {
    generateIntroductionAudioPreview(
      scenarioName: $scenarioName
      introductionScript: $introductionScript
    ) {
      ok
      errors {
        message
      }
      audioUrl
    }
  }
`;

export const useEditScenario = (
  scenario: ListScenario,
  onClose: (isSuccess?: boolean) => void,
  initialTab: "basic" | "scenario" | "character" | null,
) => {
  const initialState = useMemo(() => {
    return stateFromScenario(scenario, initialTab);
  }, [scenario, initialTab]);

  const [state, dispatch] = useReducer(reducer, initialState);

  const [editScenario, { loading }] = useEditScenarioMutation();
  const isDirty = useMemo(() => {
    const { tab: _t, ...formState } = state;

    const { tab: _t2, ...initialFormState } = initialState;
    return JSON.stringify(formState) !== JSON.stringify(initialFormState);
  }, [state, initialState]);

  const isInvalid = useMemo(() => {
    return (
      state.name === "" ||
      state.context === "<p></p>" ||
      state.objective === "<p></p>" ||
      state.persona.name === "" ||
      state.persona.voiceId === "" ||
      (Object.keys(state.persona.personalityTraits).length === 0 &&
        state.persona.personalityCharacteristics?.length === 1 &&
        state.persona.personalityCharacteristics?.[0] === "") ||
      state.persona.backstory === "<p></p>" ||
      state.persona.situationalUnderstanding === "<p></p>"
    );
  }, [state]);

  const disabled = useMemo(() => {
    return !isDirty || isInvalid;
  }, [isDirty, isInvalid]);

  const [generateIntroductionAudioPreview] = useMutation(
    GENERATE_INTRODUCTION_AUDIO_PREVIEW_MUTATION,
  );

  const handleSave = useCallback(() => {
    const {
      name,
      context,
      objective,
      description,
      difficulty,
      persona,
      introductionScript,
      introductionAudioUrl,
      tags,
      presentations,
      enableScreenSharing,
    } = state;

    editScenario({
      variables: {
        slug: scenario?.slug,
        name: name ?? "",
        context: convertToMarkdown(context ?? ""),
        objective: convertToMarkdown(objective ?? ""),
        description: description ?? "",
        difficulty: difficulty ?? ScenarioDifficulty.Custom,
        introductionScript: introductionScript ?? "",
        introductionAudioUrl: introductionAudioUrl ?? "",
        persona: {
          name: persona.name ?? "",
          jobTitle: persona.jobTitle ?? "",
          company: persona.company ?? "",
          voiceId: persona.voiceId ?? "",
          characterHeadshotId: persona.characterHeadshotId || null, // Send null to clear the association
          personalityCharacteristics:
            persona.personalityCharacteristics?.join("\n- "),
          personalityTraits: Object.entries(persona.personalityTraits).map(
            ([attributeId, levelId]) => ({
              attributeId,
              levelId,
            }),
          ),
          openingLine: persona.openingLine ?? "",
          backstory: convertToMarkdown(persona.backstory ?? ""),
          situationalUnderstanding: convertToMarkdown(
            persona.situationalUnderstanding ?? "",
          ),
        },
        tags: tags?.map((tag) => ({
          id: tag.__isNew__ ? null : tag.value,
          name: tag.label,
        })),
        fileIds: presentations.map((file) => file.id.toString()),
        enableScreenSharing,
      },
      onCompleted: (response) => {
        if (response?.editScenario?.ok) {
          onClose(true);
        } else {
          dispatch({
            type: "setError",
            error:
              response?.editScenario?.errors
                ?.map((e) => e?.message)
                .join(", ") ?? "Unknown error",
            initialScenario: scenario,
          });
        }
      },
      onError: (error) => {
        dispatch({
          type: "setError",
          error: error.message,
          initialScenario: scenario,
        });
      },

      refetchQueries: [
        {
          query: ScenarioDocument,
          variables: { scenarioSlug: scenario?.slug },
        },
      ],
    });
  }, [state, scenario, editScenario, onClose]);

  const handlePreviewClick = useCallback(async () => {
    if (state.audioPreview === "dirty") {
      dispatch({
        type: "setAudioPreview",
        audioPreview: "generating",
      });
      const { data } = await generateIntroductionAudioPreview({
        variables: {
          scenarioName: scenario?.name ?? "",
          introductionScript: state.introductionScript ?? "",
        },
      });
      if (data?.generateIntroductionAudioPreview.ok) {
        dispatch({
          type: "setIntroductionAudioUrl",
          introductionAudioUrl: data.generateIntroductionAudioPreview.audioUrl,
        });
      } else {
        dispatch({
          type: "setError",
          error: data?.generateIntroductionAudioPreview.errors
            ?.map((e: { message: string }) => e.message)
            .join(", "),
          initialScenario: scenario,
          audioPreview: "none",
        });
      }
    }
  }, [
    generateIntroductionAudioPreview,
    state.audioPreview,
    state.introductionScript,
    scenario,
  ]);

  return {
    state,
    dispatch,
    disabled,
    handleSave,
    loading,
    isDirty,
    handlePreviewClick,
  };
};

export type { action, state };
